import { Button, ELanguage, Utils, TrustedCompanyName } from '@microfrontends/react-components'
import Image from 'next/image'
import React, { useCallback, useEffect, useState } from 'react'
import { buildCompanyUrl, showLoginModal, slugify } from '../../helpers/common'
import { companyFollow } from '../../services/companyDetailService'
import { DEFAULT_SUCCESS_CODE } from '../../services/jobService'
import { useStore } from '../../store'
import { ICompanyListItem } from '../CompanyListPage/type'
import { EMPTY_COMPANY_IMAGE } from '../constants'
import BriefcaseIcon from '../Icons/BriefcaseIcon'
import FolderIcon from '../Icons/FolderIcon'
import FollowersIcon from '../Icons/FollowersIcon'
import {
    ContentSection,
    ActionSection,
    CompanyItemWrapper,
    LogoSection,
    CompanyDescription,
    CompanyJobCount,
    GroupContent
} from './CompanySearchItem.css'

export interface ICompanyItemProps {
    companyInfo: ICompanyListItem | null
}

type IIndustryV3Info = {
    industryV3Id: number
    industryV3Name: string
    industryV3NameVI: string
}

type IIndustryV1Info = {
    industryId: number
    industryName: string
    industryNameVI: string
}

type IIndustryInfo = IIndustryV1Info | IIndustryV3Info

const CompanySearchItem = (props: ICompanyItemProps) => {
    const { companyInfo } = props
    const { user, t, langCode, isMobile } = useStore().state

    const [isFollow, setFollowed] = useState<boolean>(companyInfo?.isFollowed === 1 || false)
    const [followCount, setFollowCount] = useState<number>(companyInfo?.followerCount || 0)
    const [companyLogoImage, setCompanyLogoImage] = useState<string>(
        companyInfo && companyInfo?.companyLogoURL !== '' ? companyInfo?.companyLogoURL : EMPTY_COMPANY_IMAGE
    )

    let companyUrl = buildCompanyUrl(companyInfo?.companyId || 0, slugify(companyInfo?.companyName || ''), langCode)
    const isCCPAndOCI = companyInfo?.profilePublishedSiteMask === 1 || companyInfo?.profilePublishedSiteMask === 3

    if (companyInfo?.profilePublishedSiteMask && isCCPAndOCI) {
        companyUrl = `/company/${companyInfo?.companySlug}`
    }

    const doFollow = useCallback(
        async (isFollowed: boolean) => {
            if (!user) {
                showLoginModal(langCode)
                return
            }
            if (user?.accessToken === null || user?.accessToken === '') {
                showLoginModal(langCode)
                return
            }
            if (companyInfo === null || !companyInfo.companyId) {
                return
            }
            const response = await companyFollow(companyInfo?.companyId, isFollowed, user?.accessToken || '', langCode)
            if (response?.code !== DEFAULT_SUCCESS_CODE) {
                return
            }
            setFollowed(isFollowed)
            setFollowCount(isFollowed ? followCount + 1 : followCount - 1)
            let toastTitle = t('Unfollowed')
            let toastMessage = t("You'll stop receive the newest jobs from this company.")
            if (isFollowed) {
                toastTitle = t('Yay! Thanks for following')
                toastMessage = t('You will receive the newest jobs from this company.')
            }
            Utils.showToast(toastTitle, toastMessage, 'success')
        },
        [companyInfo, followCount, langCode, t, user]
    )

    const handleOnClickFollow = useCallback(() => {
        const followed = !isFollow
        doFollow(followed)
    }, [doFollow, isFollow])

    useEffect(() => {
        if (companyInfo?.followerCount) {
            setFollowCount(companyInfo?.followerCount)
        }

        if (companyInfo) {
            let companyLogo = companyInfo?.companyLogoURL !== '' ? companyInfo?.companyLogoURL : EMPTY_COMPANY_IMAGE
            if (isCCPAndOCI) {
                companyLogo =
                    companyInfo.companyLogoUrlCCP !== '' ? companyInfo.companyLogoUrlCCP : companyInfo.companyLogoURL
            }
            if (companyLogo === '') {
                companyLogo = EMPTY_COMPANY_IMAGE
            }
            setCompanyLogoImage(companyLogo)
        }
    }, [companyInfo])

    let industries: any[] = []
    if (companyInfo) {
        // FIXME - current assumption is using CCP first, fallback to normal industry
        industries = (companyInfo.industriesV3CCP || companyInfo.industriesV3)
            ?.map((industry: any) =>
                langCode === ELanguage.VI
                    ? industry.industryV3NameVI
                    : industry.industryV3Name
            )
            ?.filter((industryName) => !!industryName) || []
        if (industries.length) {
            // @ts-ignore
            industries = [...new Set(industries)]
        }
    }

    let companyName = companyInfo?.companyName
    if (companyInfo?.profilePublishedSiteMask && isCCPAndOCI && companyInfo?.companyNameCCP !== '') {
        companyName = companyInfo?.companyNameCCP
    }

    return (
        <CompanyItemWrapper>
            <GroupContent>
                <LogoSection href={companyUrl}>
                    <Image
                        src={companyLogoImage}
                        alt={companyName}
                        priority
                        layout='fill'
                        placeholder='blur'
                        blurDataURL={EMPTY_COMPANY_IMAGE}
                        onError={() => {
                            setCompanyLogoImage(EMPTY_COMPANY_IMAGE)
                        }}
                    />
                </LogoSection>
                <ContentSection isShowFollow>
                    <TrustedCompanyName
                        href={companyUrl}
                        name={companyName || ''}
                        isTrusted={companyInfo?.profilePublishedSiteMask === 1}
                        style={{ marginBottom: 4 }}
                        tooltip={t('Trusted company')}
                    />
                    {industries && industries?.length > 0 ? (
                        <CompanyDescription isFolderIcon>
                            <span>
                                <FolderIcon /> {industries?.join(', ')}
                            </span>
                        </CompanyDescription>
                    ) : null}
                    <CompanyDescription>
                        <span>
                            <FollowersIcon style={{ marginRight: 4 }} /> {followCount}{' '}
                            {companyInfo && companyInfo?.followerCount > 1 ? t('followers') : t('follower')}
                        </span>
                        {companyInfo && companyInfo?.onlineJobCount > 0 && (
                            <CompanyJobCount>
                                <BriefcaseIcon />
                                {companyInfo?.onlineJobCount}{' '}
                                {companyInfo?.onlineJobCount > 1 ? t('opening jobs') : t('opening job')}
                            </CompanyJobCount>
                        )}
                    </CompanyDescription>
                </ContentSection>
            </GroupContent>
            <ActionSection>
                <Button type='primary' isOutline size={isMobile ? 'md' : 'sm'} onClick={handleOnClickFollow}>
                    {isFollow ? t('Following') : t('Follow')}
                </Button>
            </ActionSection>
        </CompanyItemWrapper>
    )
}

export default CompanySearchItem
